import React, { useState, useEffect, useRef } from 'react';
import {MferHead, isDeadPiece, isNormalPiece, isLobesy, turnAlivePieceDead} from './MferHead';
import { horizontalMatchPositions, verticalMatchPositions } from '../gameLogic';
import GameOverModal from './GameOverModal';
import Timer from './Timer';
import LoggedIn from './LoggedIn';
import BackgroundMusic from './BackgroundMusic';
import CONSTANTS from '../constants';
import NameModal from './NameModal';
import { v4 as uuidv4 } from 'uuid';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import './Board.css';
import { Howl } from 'howler';
import Cookie from 'js-cookie';


function Board({ config, twitterUser, reloadBoardKey }) {
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const AUTOPLAY = config.AUTOPLAY || false;
    let AUTOPLAY_INTERVAL_MS = config.AUTOPLAY_INTERVAL_MS || 500;
    AUTOPLAY_INTERVAL_MS = AUTOPLAY_INTERVAL_MS < 100 ? 100 : AUTOPLAY_INTERVAL_MS;
    const BOARD_SIZE = config.BOARD_SIZE;
    const LEVEL_NAME = config.LEVEL_NAME;
    const LEVEL_ID = config.ID;
    const levelClass = `level-${LEVEL_ID}`;
    const BG_AUDIOS = config.BG_AUDIOS;
    const END_AUDIOS = config.END_AUDIOS || ["/mfersbeheaded/audio/shaya-beatbox-2.mp3"];
    const BG_IMAGE = config.BG_IMAGE;
    const BASIC_SOUND = config.BASIC_SOUND;
    const WRONG_SOUND = CONSTANTS.WRONG_SOUND;
    const ADV_SOUND_CLIPS = config.ADV_SOUND_CLIPS;
    const TIME_LIMIT = config.TIME_LIMIT;
    const IMAGE_MAP = config.IMAGE_MAP;
    const LOBESY_ENABLED = config.LOBESY_ENABLED || false;
    const VERSION = config.VERSION || "1.0";
    const IS_TEST_LEVEL = config.IS_TEST || false;
    const BOARD_INITIAL_STATE = config.BOARD_INITIAL_STATE || null;
    const levelDetails = {
        LEVEL_NAME: config.LEVEL_NAME,
        LEVEL_ID: LEVEL_ID,
        LEVEL_VERSION: config.VERSION,
        CREDITS: config.CREDITS || [],
        TIME_LIMIT: TIME_LIMIT,
    }
    const basicSound = new Howl({
        src: [BASIC_SOUND], // Replace with your sound file path
        autoplay: false, // Play the sound right away
        loop: false, // Do not loop the sound
        volume: 0.5, // Set the volume to 50%
    });
    const lobesySound = new Howl({
        src: [config.LOBESY_AUDIO || CONSTANTS.LOBESY_AUDIO], // Replace with your sound file path
        autoplay: false, // Play the sound right away
        loop: false, // Do not loop the sound
        volume: 0.5, // Set the volume to 50%
    });
    const wrongSound = new Howl({
        src: [WRONG_SOUND], // Replace with your sound file path
        autoplay: false, // Play the sound right away
        loop: false, // Do not loop the sound
        volume: 0.5, // Set the volume to 50%
    });
    const navigate = useNavigate();
    const audioRef = useRef();
    const sanitizeUserName = (userName) => {
        if (!userName) return null;
        
        const decodedName = decodeURIComponent(userName);
        
        if (decodedName.length > 20) return null;
        
        // You can add other sanitization checks here if needed.
        return decodedName;
    };

    // game state
    const [endTime, setEndTime] = useState(null);
    const [bit, setBit] = useState(false);
    const [startTime, setStartTime] = useState(null);
    const [gameState, setGameState] = useState(CONSTANTS.GAME_STATE_NOT_YET_BEGUN);
    const [forceHideGameOverModal, setForceHideGameOverModal] = useState(false);
    const [isNameModalOpen, setNameModalOpen] = useState(true);
    const [searchParams] = useSearchParams();
    const userNameFromQueryParam = sanitizeUserName(searchParams.get('userName'));
    const [musicMuted, setIsMusicMuted] = useState(() => Cookie.get('musicMuted') === 'true' ? true : false);   
    const [name, setName] = useState(userNameFromQueryParam || null);
    const [gamePlayUUID] = useState(uuidv4());
    function elapsedTimeSince() {
        if (startTime === null) {
            return 1; // very small time but still above 0
        }
        return new Date().getTime() - startTime;
    }    
    const handleNameConfirmation = (name) => {
        console.log("User's name:", name);
        setName(name);
        setNameModalOpen(false);
    };
    useEffect(() => {
        Cookie.set('musicMuted', musicMuted, { expires: 7 });
    }, [musicMuted]);
    
    useEffect(() => {
        const intervalId = setInterval(() => {
          setBit((prevBit) => !prevBit); // Flip the bit
        }, 3000); // 3 seconds
        
        // Cleanup function to clear the interval when the component unmounts
        return () => clearInterval(intervalId);
      }, []); // Empty dependency array means this effect runs once when the component mounts
      
    function useForceUpdate() {
        const [, setValue] = useState(0);
        return () => setValue(value => value + 1);
    }
      
    const forceUpdate = useForceUpdate();

    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const initializeBoard = () => {
        const board = [];
        let stateChanges = [];
        let queue = [];
        if (BOARD_INITIAL_STATE !== null) {
            //console.log("initializing board hardcoded instead of random");
            queue = [...BOARD_INITIAL_STATE];
        } else {
            for (let i = 0; i < BOARD_SIZE * BOARD_SIZE; i++) {
                queue.push(Math.floor(Math.random() * (CONSTANTS.N_MFERHEADS-1)) + 1);
            }
        }
        for (let i = 0; i < BOARD_SIZE; i++) {
            const row = [];
            for (let j = 0; j < BOARD_SIZE; j++) {
                let val = queue.pop(0);
                row.push(val);
                stateChanges.push([i, j, val]);
            }
            board.push(row);
        }
        return [board, stateChanges];
    }
    function playSound() {
        if (!musicMuted) {
            basicSound.play();
        }
    }
    function playWrongSound() {
        if (!musicMuted) {
            wrongSound.play();
        }
    }

    const fillWithNewJewels = (board) => {
        const jewelTypes = [1, 2, 3, 4, 5, 6];  // Assuming 6 types of jewels, you can adjust this as needed
        let stateChanges = [];
        let nFilled = 0;
        for (let col = 0; col < BOARD_SIZE; col++) {
            for (let row = 0; row < BOARD_SIZE; row++) {
                if (isDeadPiece(board[row][col])) {
                    //console.log("filling ", row , col, " type ", board[row][col] ,  " with new jewel");
                    board[row][col] = jewelTypes[Math.floor(Math.random() * CONSTANTS.N_MFERHEADS)];
                    stateChanges.push([row, col, board[row][col]])
                    nFilled += 1;
                }
            }
        }
        console.log("Filled ", nFilled, " more jewels");
        return [nFilled, stateChanges];
    };
    
    const applyGravity = (board) => {
        let stateChanges = [];
        for (let col = 0; col < BOARD_SIZE; col++) {  // Assuming an 8x8 board
            let emptyRow = (BOARD_SIZE-1);  // Start from the bottom
            for (let row = (BOARD_SIZE-1); row >= 0; row--) {
                if (isDeadPiece(board[row][col])) {
                    let nonEmptyRowAbove = row - 1;
                    while (nonEmptyRowAbove >= 0 && isDeadPiece(board[nonEmptyRowAbove][col])) {
                        nonEmptyRowAbove--;
                    }
    
                    if (nonEmptyRowAbove < 0) { // No more non-empty jewels in this column
                        break;
                    }
    
                    board[emptyRow][col] = board[nonEmptyRowAbove][col];
                    stateChanges.push([emptyRow, col, board[emptyRow][col]]);
                    // if not already dead, set it to dead version
                    if (!isDeadPiece(board[nonEmptyRowAbove][col])) {
                        board[nonEmptyRowAbove][col] = turnAlivePieceDead(board[nonEmptyRowAbove][col])
                        stateChanges.push([nonEmptyRowAbove, col, board[nonEmptyRowAbove][col]]);
                    }
                    
                    emptyRow--;
                } else {
                    emptyRow--;
                }
            }
        }
        return stateChanges;
    };
    
    
    const hasHorizontalMatchAt = (board, row, col) => {
        const jewelType = board[row][col];
        let matchCount = 0;
    
        for (let i = col; i < BOARD_SIZE; i++) {
            if (board[row][i] === jewelType) matchCount++;
            else break;
        }
    
        for (let i = col - 1; i >= 0; i--) {
            if (board[row][i] === jewelType) matchCount++;
            else break;
        }
    
        return matchCount >= 3;
    };
    
    const hasVerticalMatchAt = (board, row, col) => {
        const jewelType = board[row][col];
        let matchCount = 0;
    
        for (let i = row; i < BOARD_SIZE; i++) {
            if (board[i][col] === jewelType) matchCount++;
            else break;
        }
    
        for (let i = row - 1; i >= 0; i--) {
            if (board[i][col] === jewelType) matchCount++;
            else break;
        }
    
        return matchCount >= 3;
    };
    function setDifference(setA, setB) {
        let difference = new Set(setA);
        for (let elem of setB) {
            difference.delete(elem);
        }
        return difference;
    }
    const findSwapForMatch = (board, selectedJewel = null) => {
        // Create a deep copy of the board
        const copiedBoard = board.map(row => [...row]);
    
        const startRow = selectedJewel ? selectedJewel.rowIndex : 0;
        const endRow = selectedJewel ? selectedJewel.rowIndex + 1 : BOARD_SIZE;
    
        let bestSwap = null; // Store the best swap here
        let highestPriority = 0; // Store the priority of the best swap (5 matches = 5, 4 matches = 4, etc.)
    
        for (let row = endRow - 1; row >= startRow; row--) {
            const startCol = selectedJewel ? selectedJewel.colIndex : 0;
            const endCol = selectedJewel ? selectedJewel.colIndex + 1 : BOARD_SIZE;
    
            for (let col = startCol; col < endCol; col++) {
                if (board[row][col] === 7) {
                    return { row, col };
                }
    
                const neighbors = [
                    { row: row + 1, col: col },  
                    { row: row - 1, col: col },  
                    { row: row, col: col - 1 },  
                    { row: row, col: col + 1 }   
                ];
    
                for (const neighbor of neighbors) {
                    if (neighbor.row >= 0 && neighbor.row < BOARD_SIZE && neighbor.col >= 0 && neighbor.col < BOARD_SIZE) {
                        const temp = copiedBoard[row][col];
                        copiedBoard[row][col] = copiedBoard[neighbor.row][neighbor.col];
                        copiedBoard[neighbor.row][neighbor.col] = temp;
    
                        const [horizontalMatches] = horizontalMatchPositions(copiedBoard, row, col, BOARD_SIZE);
                        const [verticalMatches] = verticalMatchPositions(copiedBoard, row, col, BOARD_SIZE);
    
                        copiedBoard[neighbor.row][neighbor.col] = copiedBoard[row][col];
                        copiedBoard[row][col] = temp;
    
                        const maxMatch = Math.max(horizontalMatches.length, verticalMatches.length);
                        if (maxMatch >= 3 && maxMatch > highestPriority) {
                            bestSwap = selectedJewel ? { row: neighbor.row, col: neighbor.col } : { row, col };
                            highestPriority = maxMatch;
                            if (highestPriority === 5) { // Exit early if we've found a match of 5
                                return bestSwap;
                            }
                        }
                    }
                }
            }
        }
    
        return bestSwap;
    };
    
    
    const sweepBoardForMatches = (board, livePlay) => {
        const matchedPositions = new Set();
        const powerUpFiveRunPosition = new Set();
        let stateChanges = [];
    
        // Check for matches in the entire board
        for (let row = 0; row < BOARD_SIZE; row++) {
            for (let col = 0; col < BOARD_SIZE; col++) {
                const [horizontalMatches, isCenterHorizontal] = horizontalMatchPositions(board, row, col, BOARD_SIZE);
                const [verticalMatches, isCenterVertical] = verticalMatchPositions(board, row, col, BOARD_SIZE);
                if (LOBESY_ENABLED && livePlay && ((horizontalMatches.length >= 5 && isCenterHorizontal) || (isCenterVertical && verticalMatches.length >= 5))) {
                    console.log("found a 5 or more run");
                    powerUpFiveRunPosition.add(`${row}-${col}`);
                    continue;
                }
                if (LOBESY_ENABLED && livePlay && ((horizontalMatches.length >= 3) && (verticalMatches.length >= 3))) {
                    console.log("found a 5 or more run");
                    powerUpFiveRunPosition.add(`${row}-${col}`);
                    continue;
                }

                horizontalMatches.forEach(match => matchedPositions.add(`${match.row}-${match.col}`));
                verticalMatches.forEach(match => matchedPositions.add(`${match.row}-${match.col}`));
                // if there's either a horizontal or vertial match, set it to be cleared
                if (horizontalMatches.length !== 0 || verticalMatches.length !== 0) {
                    addItemToClear(col, row);
                }
            }
        }
        // Set the focus position 
        powerUpFiveRunPosition.forEach(pos => {
            const [row, col] = pos.split('-').map(Number);
            board[row][col] = 7;
            stateChanges.push([row, col, board[row][col]]);
        });
        // diff the matched with the focus to not clear them
        // Clear the matched jewels from the board
        const positionToDelete = setDifference(matchedPositions, powerUpFiveRunPosition);
        positionToDelete.forEach(pos => {
            const [row, col] = pos.split('-').map(Number);
            //console.log("clearing ", row, col);
            board[row][col] = turnAlivePieceDead(board[row][col]);
            stateChanges.push([row, col, board[row][col]]);
        });

        clearItems();
    
        return [matchedPositions.size, stateChanges]; // Return true if matches were found, false otherwise
    };
    const computeNDeadPieces = (board) => {
        let nDeadPieces = 0;
        for (let row = 0; row < board.length; row++) {
            for (let col = 0; col < board[row].length; col++) {
                if (isDeadPiece(board[row][col])) {
                    nDeadPieces += 1;
                }
            }
        }
        return nDeadPieces;
    };

    const sweepBoard = async (board, gameState, livePlay=false) => {
        let stateChanges = [];
        let [nClearedThisRound, stateChangesFromSweeps] = sweepBoardForMatches(board, livePlay);
        if (livePlay) {
            stateChanges.push({t: elapsedTimeSince(), changes: stateChangesFromSweeps});
        }
        let nCleared = nClearedThisRound;
        let shouldContinue = (nCleared > 0 || computeNDeadPieces(board));
        while (shouldContinue) {
            if (livePlay) {
                playSound();
                await delay(300);

                if (nClearedThisRound > 3) {
                    playAdvancedSoundNTimes(1);
                    await delay(300);
                }
            }
            console.log("  cleared ", nClearedThisRound, " matches");
            const stateChangesFromGravity = applyGravity(board);
            if (livePlay) {
                stateChanges.push({t: elapsedTimeSince(), changes: stateChangesFromGravity});
            }
            const [, stateChangesromJewels] =  fillWithNewJewels(board);
            if (livePlay) {
                stateChanges.push({t: elapsedTimeSince(), changes: stateChangesromJewels});
            }
            await delay(150);
            [nClearedThisRound, stateChangesFromSweeps] = sweepBoardForMatches(board, livePlay);
            if (livePlay) {
                stateChanges.push({t: elapsedTimeSince(), changes: stateChangesFromSweeps});
            }
            nCleared += nClearedThisRound;
            shouldContinue = nClearedThisRound > 0 || computeNDeadPieces(board);

        }
        return [nCleared, stateChanges];
    }
    const applyLobesy = async (board, rowIndex, colIndex) => {
        let stateChanges = [];
        if (!isLobesy(board[rowIndex][colIndex])) {
            return [0, stateChanges];
        }
    
        let deadCount = 0;
        const numRows = BOARD_SIZE;
        const numCols = BOARD_SIZE;

        // Mark cells in the same column as dead
        for (let r = 0; r < numRows; r++) {
            if (isNormalPiece(board[r][colIndex])) {
                board[r][colIndex] = turnAlivePieceDead(board[r][colIndex]);
                stateChanges.push([r, colIndex, board[r][colIndex]]);
                deadCount++;
            }
        }
        forceUpdate();

        // Mark cells in the same row as dead
        for (let c = 0; c < numCols; c++) {
            if (c !== colIndex && isNormalPiece(board[rowIndex][c])) { // Check to avoid marking the same cell twice
                board[rowIndex][c] = turnAlivePieceDead(board[rowIndex][c]);
                stateChanges.push([rowIndex, c, board[rowIndex][c]]);
                deadCount++;
            }
        }
        forceUpdate();
    
        // Mark the original cell (from where the Lobesy effect is originating) as dead
        board[rowIndex][colIndex] = turnAlivePieceDead(board[rowIndex][colIndex]);
        stateChanges.push([rowIndex, colIndex, board[rowIndex][colIndex]]);
        return [deadCount + 1, stateChanges];  // +1 for the original cell
    };
    const [initBoard, initStateChanges] = initializeBoard();
    const [board,] = useState(initBoard);
    const [selectedJewel, setSelectedJewel] = useState(null);
    const [clickedJewel, setClickedJewel] = useState(null);
    const initGameStats = {
        totalScore: 0,
        powerUps: 0,
        scoreChanges: [],
        boardStateChanges: [{t: 0, changes: initStateChanges}],
        level: LEVEL_NAME,
        level_version: VERSION,
    };
    useEffect(() => {
        async function handleJewelClick() {
            if (clickedJewel) {
                const { rowIndex, colIndex } = clickedJewel;
                if (gameState === CONSTANTS.GAME_STATE_COMPLETE) {
                    console.log("game has ended");
                    return;
                }
                if (gameState === CONSTANTS.GAME_STATE_NOT_YET_BEGUN) {
                    if (TIME_LIMIT != null) {
                        const startTime = new Date().getTime();
                        console.log("Start time set to ", startTime);
                        setStartTime(startTime);
                        setEndTime(startTime + (TIME_LIMIT * 1000));
                        setGameState(CONSTANTS.GAME_STATE_IN_PROGRESS);
                    } else {
                        setGameState(CONSTANTS.GAME_STATE_IN_PROGRESS);                
                    }
                }
        
                // handle Lobesy
                if (selectedJewel && isLobesy(board[rowIndex][colIndex])) {
                    console.log("cannot move onto a lobesy, releasing " + rowIndex + ", " + colIndex);
                    setSelectedJewel(null);
                    return;
                }
                if (!selectedJewel && isLobesy(board[rowIndex][colIndex])) {
                    setSelectedJewel({ rowIndex, colIndex });
                    playLobesySound();
                    const [nClearedFromLobesy, stateChangesFromLobesy] = await applyLobesy(board, rowIndex, colIndex);
                    if (gameState !== CONSTANTS.GAME_STATE_IN_PROGRESS || gameState !== CONSTANTS.GAME_STATE_NOT_YET_BEGUN) {
                        setGameStats(prevStats => ({
                            ...prevStats,
                            totalScore: prevStats.totalScore + nClearedFromLobesy,
                            powerUps: prevStats.powerUps + 1,
                            scoreChanges: [...prevStats.scoreChanges, {
                                score: prevStats.totalScore + nClearedFromLobesy,
                                t: elapsedTimeSince(),
                            }],
                            // boardStateChanges: [...prevStats.boardStateChanges, {
                            //     t: elapsedTimeSince(),
                            //     changes: stateChangesFromLobesy,
                            // }]
                        }));
                    }
                    for (let i = 0; i < 4; i++) {
                        playSound();
                        await delay(150);
                    }
                    applyGravity(board);
                    fillWithNewJewels(board);
                    const stateChangesFromGravity = applyGravity(board);
                    // setGameStats(prevStats => ({
                    //     ...prevStats,
                    //     boardStateChanges: [...prevStats.boardStateChanges, {t: elapsedTimeSince(), changes: stateChangesFromGravity}]
                    // }));
                    const [, stateChangesromJewels] =  fillWithNewJewels(board);
                    // setGameStats(prevStats => ({
                    //     ...prevStats,
                    //     boardStateChanges: [...prevStats.boardStateChanges, {t: elapsedTimeSince(), changes: stateChangesromJewels}]
                    // }));
        
                    let [nClearedThisRound, stateChangesFromSweepBoard] = await sweepBoard(board, gameState, true);   
                    if (gameState !== CONSTANTS.GAME_STATE_IN_PROGRESS || gameState !== CONSTANTS.GAME_STATE_NOT_YET_BEGUN) {        
                        setGameStats(prevStats => ({
                            ...prevStats,
                            totalScore: prevStats.totalScore + nClearedThisRound,
                            powerUps: prevStats.powerUps + 1,
                            scoreChanges: [...prevStats.scoreChanges, {
                                score: prevStats.totalScore + nClearedThisRound,
                                t: elapsedTimeSince(),
                            }],
                            // boardStateChanges: [...prevStats.boardStateChanges, ...stateChangesFromSweepBoard]
                        }));
                    }
                    setSelectedJewel(null);
                    return;
                }
        
                if (!selectedJewel) {
                    console.log("selecting " + rowIndex + ", " + colIndex + ", which is of type:" + board[rowIndex][colIndex]);
                    setSelectedJewel({ rowIndex, colIndex });
                    return;
                }
            
                const isAdjacent = Math.abs(selectedJewel.rowIndex - rowIndex) + Math.abs(selectedJewel.colIndex - colIndex) === 1;
            
                
                if (isAdjacent) {
                    console.log("is adjacent, now checking for valid swap " + rowIndex + ", " + colIndex);
                    const newBoard = [...board];
                    const temp = newBoard[rowIndex][colIndex];
                    newBoard[rowIndex][colIndex] = newBoard[selectedJewel.rowIndex][selectedJewel.colIndex];
                    newBoard[selectedJewel.rowIndex][selectedJewel.colIndex] = temp;
            
                    // Check if the swap leads to a match
                    const isValidSwap = hasHorizontalMatchAt(newBoard, rowIndex, colIndex) ||
                                        hasVerticalMatchAt(newBoard, rowIndex, colIndex) ||
                                        hasHorizontalMatchAt(newBoard, selectedJewel.rowIndex, selectedJewel.colIndex) ||
                                        hasVerticalMatchAt(newBoard, selectedJewel.rowIndex, selectedJewel.colIndex);
                                        
                    if (isValidSwap) {
                        console.log("Valid swap between  (", rowIndex, ", ", colIndex, ") and (", selectedJewel.rowIndex, ",", selectedJewel.colIndex, ")");
                        const changeFromSwap = {t: elapsedTimeSince(), changes: [[rowIndex, colIndex, board[rowIndex][colIndex]], [selectedJewel.rowIndex,  selectedJewel.colIndex, board[selectedJewel.rowIndex][selectedJewel.colIndex]]]};
                        // record state changes
                        setSelectedJewel(null);
                        let [nScored, stateChangesFromSweep] = await sweepBoard(board, gameState, true);
                        if (gameState !== CONSTANTS.GAME_STATE_IN_PROGRESS || gameState !== CONSTANTS.GAME_STATE_NOT_YET_BEGUN) { 
                            setGameStats(prevStats => ({
                                ...prevStats,
                                totalScore: prevStats.totalScore + nScored,
                                scoreChanges: [...prevStats.scoreChanges, {
                                    score: prevStats.totalScore + nScored,
                                    t: elapsedTimeSince(),
                                }],
                                // boardStateChanges: [...prevStats.boardStateChanges, changeFromSwap,...stateChangesFromSweep]
                            }));
                        }
                    } else {  
                        playWrongSound();
                        console.log("NOT a valid swap, reverting " + rowIndex + ", " + colIndex);
                        const temp = newBoard[rowIndex][colIndex];
                        newBoard[rowIndex][colIndex] = newBoard[selectedJewel.rowIndex][selectedJewel.colIndex];
                        newBoard[selectedJewel.rowIndex][selectedJewel.colIndex] = temp;   
                    }  
                } else {
                    playWrongSound();
                    console.log("not adjacent, releasing " + rowIndex + ", " + colIndex);
                }
            
                setSelectedJewel(null);
            };

        }

        handleJewelClick();

    }, [clickedJewel]);

    useEffect(() => {
        if (!AUTOPLAY) {
            return; // Exit early if AUTOPLAY is false
        }
    
        // Introduce delay here
        const timerId = setTimeout(() => {
            const output = findSwapForMatch(board, selectedJewel);
            console.log(" AI Agent proposes ", output);
    
            if (gameState === CONSTANTS.GAME_STATE_IN_PROGRESS) {
                if (output === null) {
                    setClickedJewel(null);
                } else {
                    setClickedJewel({ rowIndex: output.row, colIndex: output.col});    
                }
            }
        }, AUTOPLAY_INTERVAL_MS); // Delay
    
        // Cleanup function
        return () => {
            clearTimeout(timerId);
        };
    }, [board, selectedJewel, gameState, bit]);


    const [gameStats, setGameStats] = useState(initGameStats);

    const [itemsToClear, setItemsToClear] = useState([]);
    const addItemToClear = (x, y) => {
        setItemsToClear(prevItems => [...prevItems, { x, y }]);
    }
    const clearItems = () => {
        setItemsToClear([]);
    }
    const isJewelToBeCleared = (x, y) => {
        return itemsToClear.some(item => item.x === x && item.y === y);
    }

    function playAdvancedSoundNTimes(n) {
        if (musicMuted) {
            return;
        }
        if (ADV_SOUND_CLIPS.length === 0) {
            return;
        }
        const randomIndex = Math.floor(Math.random() * ADV_SOUND_CLIPS.length);
        let snd = new Howl({
            src: [ADV_SOUND_CLIPS[randomIndex]], // Replace with your sound file path
            autoplay: false, // Play the sound right away
            loop: false, // Do not loop the sound
            volume: 0.5, // Set the volume to 50%
        });
        snd.play();
    }
    function playLobesySound() {
        if (!musicMuted) {
            lobesySound.play();
        }
    }
    const navigateWithUserName = () => {

        if (twitterUser && twitterUser.screen_name) {
            reloadBoardKey();
            return;
        }
        const currentUrl = new URL(window.location.href);
        
        // Extract the current hash path and existing search parameters
        let hashPath = currentUrl.hash;
        const hashParams = new URLSearchParams(hashPath.split('?')[1]);
    
        // If userName already exists, remove it to avoid duplication or to override.
        hashParams.delete("userName");
        
        if (name) {
            const encodedName = encodeURIComponent(name);
            hashParams.set("userName", encodedName);
        }
    
        // Only append the question mark and parameters if there are any.
        const paramString = hashParams.toString();
        hashPath = paramString ? `${hashPath.split('?')[0]}?${paramString}` : hashPath.split('?')[0];
        currentUrl.hash = hashPath;
        
        window.location.href = currentUrl.toString();
        window.location.reload();
    };
    
    
    useEffect(() => {
        const fetchData = async () => {
            await sweepBoard(board, gameState, false);
        }
        fetchData();
        console.log("Loading Level ", levelDetails, " test level? ", IS_TEST_LEVEL, " with uuid ", gamePlayUUID);
    }, []); /* run only once at beginning */

    useEffect(() => {
        switch (LEVEL_ID) {
            case 'nakamigo_crush':
                document.title = 'nakamigo crush';
                break;
            default:
                document.title = 'mfers beheaded';
        }
    }, [LEVEL_ID]); // The effect will run every time levelID changes

    return (
        <div className={`gameWiderContainer ${levelClass}`} style={{backgroundImage: `url(${process.env.PUBLIC_URL}/${BG_IMAGE})`}}>
        <div className={`gameContainer ${levelClass}`}>
            <div className="header">
                <div className={`levelContainer ${levelClass}`}>
                    {LEVEL_NAME}
                </div>
                <div className="statsContainer">
                    <LoggedIn 
                        twitterUser={twitterUser}
                        name={name}
                        levelID={LEVEL_ID}
                    />
                    <div className={`scoreContainer ${levelClass}`}>
                        score: {gameStats.totalScore}
                    </div>
                    <Timer 
                        endTime={endTime}
                        gameState={gameState}
                        timeLimit={TIME_LIMIT}
                        levelID={LEVEL_ID}
                        onTimeElapsed={() => {
                            setGameState(CONSTANTS.GAME_STATE_COMPLETE);
                        }}
                    />
                </div>
            </div>
            <div className={`board ${levelClass}`}>
                {board.map((row, rowIndex) => (
                    <div className="row" key={rowIndex}>
                        {row.map((jewelType, colIndex) => {
                            
                            const shouldBeCleared = isJewelToBeCleared(rowIndex, colIndex);

                            return ( <MferHead 
                                key={colIndex} 
                                type={jewelType}
                                x={rowIndex}
                                y={colIndex}
                                imageMap={IMAGE_MAP}
                                levelID={LEVEL_ID}
                                selectedJewel={selectedJewel}
                                shouldBeCleared={shouldBeCleared} // Pass this as a prop to the Jewel component
                                onClick={() => setClickedJewel({ rowIndex, colIndex })} 
                            />
                        )})}
                    </div>
                ))}
            </div>

            <div className="buttonContainer">
                <button className={`musicButton ${levelClass}`} style={{
                        backgroundColor: musicMuted ? 'grey' : 'yellow',
                    }} onClick={() => setIsMusicMuted(!musicMuted)}>
                    {musicMuted ? (
                        <div>unmute</div>
                    ) : (
                        <div>mute</div>
                    )}
                </button>
            
                {!location.pathname.includes('kids') && <button className={`back-to-home ${levelClass}`} onClick={() => navigate('/')}>main menu</button>}                
                {gameState === CONSTANTS.GAME_STATE_COMPLETE ? (
                    <button className={`play-again ${levelClass}`} onClick={() => {navigateWithUserName(name);}}>play again</button>
                ) : (
                    <button className={`end-game ${levelClass}`} onClick={() => {setGameState(CONSTANTS.GAME_STATE_COMPLETE);}}>end game</button>
                )}
            </div>


            <NameModal 
                twitterUser={twitterUser}
                isOpen={isNameModalOpen} 
                onConfirm={handleNameConfirmation} 
                anonName={name}
                setName={setName}
                levelID={LEVEL_ID}
            />

            <GameOverModal 
                twitterUser={twitterUser}
                forceClosed={forceHideGameOverModal} 
                gameState={gameState}
                gameStats={gameStats}
                name={name} 
                autoplay={AUTOPLAY}
                levelDetails={levelDetails}
                isTestLevel={IS_TEST_LEVEL}
                levelID={LEVEL_ID}
                gamePlayUUID={gamePlayUUID}
                onClose={() => setForceHideGameOverModal(true)}
            />
            <BackgroundMusic
                audioRef={audioRef}
                gameState={gameState}
                musicMuted={musicMuted}
                audios={BG_AUDIOS}
                endAudios={END_AUDIOS}
            />
                
        </div>
        </div>
    );
}

export default Board;
